/**
 * A rule refused the request. The backend answers with a machine code plus whatever context the caller
 * needs to explain it (`DELIVERY_OUT_OF_ZONE`, `PAYMENT_IN_PROGRESS`, `ORDER_AWAITING_PAYMENT`, …), and
 * a plain `Error` would throw all of that away — the operator would get "409" and no way forward.
 *
 * `data` is the whole response body: most codes carry their context at the top level, a few (the
 * awaiting-payment refusal) nest it under `data.details` (the envelope's `details` field).
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly data: Record<string, unknown> = {},
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

/**
 * Nest returns an object payload from `new HttpException({ code, ... })` verbatim as the body, so
 * the machine code sits at the top level rather than under `message`.
 */
export async function toApiError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as Record<string, unknown>;
    // The wire field is `error_code` (the agent's `ApiErrorResponseDto` envelope); `code` is only
    // the name the backend uses internally, kept as a fallback for bodies that bypass the envelope.
    const wireCode = typeof body.error_code === 'string' ? body.error_code : body.code;
    const code = typeof wireCode === 'string' ? wireCode : `HTTP_${response.status}`;
    return new ApiError(response.status, code, body);
  } catch {
    return new ApiError(response.status, `HTTP_${response.status}`);
  }
}
